import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Store, Bike, ShieldCheck, ArrowLeft } from "lucide-react";
import { API, errMsg, dashboardFor } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const FIELD = "w-full border border-stone-300 bg-white rounded-lg px-4 py-3 text-sm outline-none focus:border-[#6A1B29] transition-colors";

const CONFIG = {
  outlet: {
    icon: Store, title: "OUTLET PARTNER LOGIN", subtitle: "Access your Suvikka outlet dashboard — orders, inventory, stock requests.",
    idLabel: "Email", idType: "email", idPlaceholder: "Outlet partner email", roles: ["retail_owner"],
    deny: "This account is not a Suvikka Outlet Partner account.",
    links: [["Not a partner yet?", "/open-outlet", "Apply to open an outlet"]],
  },
  rider: {
    icon: Bike, title: "RIDER LOGIN", subtitle: "Suvikka delivery rider — view and complete your assigned deliveries.",
    idLabel: "Mobile Number", idType: "tel", idPlaceholder: "10-digit mobile", roles: ["rider"],
    deny: "This account is not a Suvikka Rider account.",
    links: [["Customer?", "/login", "Shop or sign in here"]],
  },
  admin: {
    icon: ShieldCheck, title: "ADMIN LOGIN", subtitle: "Suvikka Control Room — full platform access. Authorised personnel only.",
    idLabel: "Email", idType: "email", idPlaceholder: "Admin / staff email", roles: ["admin", "staff"],
    deny: "This account does not have admin access.",
    links: [],
  },
};

export default function RoleLogin({ kind }) {
  const cfg = CONFIG[kind];
  const Icon = cfg.icon;
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [idf, setIdf] = useState("");
  const [password, setPassword] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = kind === "rider"
        ? await API.post("/auth/rider-login", { mobile: idf.trim(), password })
        : await API.post("/auth/login", { email: idf.trim(), password });
      if (!cfg.roles.includes(data.user.role)) {
        await API.post("/auth/logout").catch(() => {});
        toast.error(cfg.deny);
        return;
      }
      setUser(data.user);
      toast.success(`Welcome, ${data.user.name}`);
      nav(dashboardFor(data.user.role));
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16 bg-stone-50" data-testid={`${kind}-login-page`}>
      <div className="w-full max-w-md bg-white border border-stone-200 rounded-2xl p-8 shadow-sm">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-stone-400 hover:text-[#6A1B29] mb-6" data-testid={`${kind}-back-link`}>
          <ArrowLeft className="w-3.5 h-3.5" /> Customer / Affiliate login
        </Link>
        <div className="flex items-center gap-3 mb-1">
          <span className="p-2.5 rounded-xl bg-[#FFF0F4] text-[#6A1B29]"><Icon className="w-5 h-5" /></span>
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29]">Suvikka</p>
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900 mt-2">{cfg.title}</h1>
        <p className="text-sm text-stone-500 mt-1 mb-6">{cfg.subtitle}</p>
        <form onSubmit={submit} className="space-y-4" data-testid={`${kind}-login-form`}>
          <div>
            <label className="text-xs text-stone-400 uppercase tracking-wider">{cfg.idLabel}</label>
            <input required type={cfg.idType} placeholder={cfg.idPlaceholder} value={idf} onChange={(e) => setIdf(e.target.value)} className={`${FIELD} mt-1`} data-testid={`${kind}-login-id`} />
          </div>
          <div>
            <label className="text-xs text-stone-400 uppercase tracking-wider">Password</label>
            <input required type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={`${FIELD} mt-1`} data-testid={`${kind}-login-password`} />
          </div>
          <button disabled={busy} data-testid={`${kind}-login-submit`}
            className="w-full bg-[#6A1B29] hover:bg-[#501325] disabled:opacity-60 text-white font-semibold py-3.5 rounded-full transition-colors">
            {busy ? "Please wait…" : "LOGIN"}
          </button>
        </form>
        <div className="mt-6 text-sm text-stone-500 space-y-2">
          {cfg.links.map(([pre, to, label]) => (
            <p key={to}>{pre} <Link to={to} className="text-[#6A1B29] font-medium" data-testid={`${kind}-link-${to.replace(/\//g, "")}`}>{label}</Link></p>
          ))}
        </div>
      </div>
    </div>
  );
}
